/**
 * Pure helpers that adapt a Database Blueprint `.data.json` sidecar into the
 * shape the Table Explorer grid consumes. Used by `useBlueprintTableData` so
 * the grid can render blueprint rows without a live-DB query.
 *
 * The sidecar stores rows as positional arrays (`unknown[][]`) aligned to its
 * `columns` list; the grid wants name-keyed objects plus a `TableExplorerColumn[]`
 * describing each column. Filtering / sorting / paging are SERVER-side in normal
 * Table Explorer mode, but sidecars are small lookup tables, so here we apply
 * them CLIENT-side over the in-memory rowset, reusing the shared pure
 * `evaluateFilterGroupsAgainstRow` evaluator for filter-operator parity.
 */

import type {
  DatabaseBlueprintSidecar,
} from '../../../database-blueprint/types/databaseBlueprintTypes';
import type {
  FilterGroup,
  PendingChange,
  SortDefinition,
  TableExplorerColumn,
} from '../../core/types/workspaceTypes';
import { evaluateFilterGroupsAgainstRow } from '../../common/utils/filterEvaluator';

/** Sidecar column metadata (subset we read here). */
export type SidecarColumn = DatabaseBlueprintSidecar['columns'][number];

/**
 * Map sidecar columns + pk into `TableExplorerColumn[]`. PK membership comes
 * from the sidecar's `pk` array; identity from `isIdentity`. FK/loose-FK
 * overlays are resolved separately by the tab via MetaInfo, so they're left
 * `false` here (the tab enriches the same way it does for live-DB columns).
 *
 * Nullability isn't carried in the sidecar; default to `true` (permissive) so
 * the cell editors offer a NULL option — the tab overrides this with the live
 * table's MetaInfo nullability when a connection is available.
 */
export function mapSidecarColumns(
  sidecar: Pick<DatabaseBlueprintSidecar, 'columns' | 'pk'>,
): TableExplorerColumn[] {
  const pkSet = new Set(sidecar.pk);
  return sidecar.columns.map((c) => ({
    name: c.name,
    dataType: c.type ?? '',
    nullable: true,
    isPrimaryKey: pkSet.has(c.name),
    isForeignKey: false,
    isIdentity: c.isIdentity === true,
  }));
}

/**
 * Convert a sidecar's positional rows (`unknown[][]`) into name-keyed objects
 * keyed by the sidecar's column names.
 */
export function mapSidecarRowsToObjects(
  sidecar: Pick<DatabaseBlueprintSidecar, 'columns' | 'rows'>,
): Record<string, unknown>[] {
  const names = sidecar.columns.map((c) => c.name);
  return sidecar.rows.map((row) => {
    const obj: Record<string, unknown> = {};
    for (let i = 0; i < names.length; i += 1) {
      obj[names[i]] = row[i];
    }
    return obj;
  });
}

/** Compare two cell values for sorting. Nulls sort last; numbers numerically. */
function compareValues(a: unknown, b: unknown): number {
  const aNull = a === null || a === undefined;
  const bNull = b === null || b === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const an = typeof a === 'number' ? a : Number(a);
  const bn = typeof b === 'number' ? b : Number(b);
  if (!Number.isNaN(an) && !Number.isNaN(bn) && an !== bn) return an - bn;
  return String(a).localeCompare(String(b));
}

/**
 * Apply client-side filters + multi-key sort to an in-memory rowset.
 *
 * Filters reuse the shared `evaluateFilterGroupsAgainstRow` evaluator so the
 * operator parity matches the SQL path as closely as the evaluator supports
 * (=, !=, <, <=, >, >=, BETWEEN, LIKE/CONTAINS, IN-style via `enum`, IS NULL/
 * IS NOT NULL, plus JSON enum sub-operators). Sort is stable and multi-key.
 */
export function applyClientSideFilterSort(
  rows: Record<string, unknown>[],
  filters: FilterGroup[],
  sorting: SortDefinition[],
): Record<string, unknown>[] {
  let out = rows;
  if (filters.length > 0) {
    out = out.filter((r) => evaluateFilterGroupsAgainstRow(r, filters));
  }
  if (sorting.length > 0) {
    // Stable multi-key sort: decorate with original index, compare by each
    // sort key in order, tie-break on original index to keep it stable.
    out = out
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        for (const s of sorting) {
          const cmp = compareValues(a.row[s.field], b.row[s.field]);
          if (cmp !== 0) return s.direction === 'desc' ? -cmp : cmp;
        }
        return a.index - b.index;
      })
      .map((d) => d.row);
  }
  return out;
}

/**
 * Does a row's primary-key columns all equal the given key map? Uses
 * loose string-equality (mirrors `filterEvaluator` / the SQL builder) so a
 * numeric PK stored as `1` matches a key carrying `"1"`.
 */
function rowMatchesPrimaryKey(
  row: Record<string, unknown>,
  pk: string[],
  key: Record<string, unknown>,
): boolean {
  return pk.every((col) => {
    const a = row[col];
    const b = key[col];
    return a === b || String(a) === String(b);
  });
}

/**
 * Apply a Table Explorer pending-change batch to an in-memory rowset, producing
 * the FULL post-edit rowset. Pure — does not mutate the input.
 *
 *  - `update` → replace `field` on the row matching `primaryKey`.
 *  - `delete` → drop the row matching `primaryKey`.
 *  - `insert` → append the new row.
 *
 * Inserts are appended in batch order. Updates/deletes are keyed by the table's
 * `pk` columns. A delete that targets the same key as an update wins (the row is
 * dropped). This is the exact merge the blueprint Save path needs before
 * regenerating the `.data.sql` upsert + sidecar.
 */
export function applyPendingChangesToRows(
  rows: Record<string, unknown>[],
  pk: string[],
  changes: PendingChange[],
): Record<string, unknown>[] {
  // Clone rows so callers' arrays/objects are never mutated.
  let working: Record<string, unknown>[] = rows.map((r) => ({ ...r }));

  // Updates first (so a later delete on the same key still removes the row).
  for (const change of changes) {
    if (change.type !== 'update') continue;
    for (const row of working) {
      if (rowMatchesPrimaryKey(row, pk, change.primaryKey)) {
        row[change.field] = change.newValue;
      }
    }
  }

  // Deletes.
  const deleteKeys = changes.filter((c): c is Extract<PendingChange, { type: 'delete' }> => c.type === 'delete');
  if (deleteKeys.length > 0) {
    working = working.filter(
      (row) => !deleteKeys.some((d) => rowMatchesPrimaryKey(row, pk, d.primaryKey)),
    );
  }

  // Inserts (appended in batch order).
  for (const change of changes) {
    if (change.type === 'insert') {
      working.push({ ...change.row });
    }
  }

  return working;
}

/**
 * Convert name-keyed row objects back into positional arrays aligned to the
 * given column-name order (the shape the sidecar + upsert builders consume).
 * Missing keys become `null`.
 */
export function rowObjectsToPositional(
  rows: Record<string, unknown>[],
  columnNames: string[],
): unknown[][] {
  return rows.map((row) =>
    columnNames.map((name) => (name in row ? row[name] : null)),
  );
}

export type BlueprintTableDataResult = {
  rows: Record<string, unknown>[];
  columns: TableExplorerColumn[];
  totalRowCount: number;
};

/**
 * Build the grid-ready result from a sidecar: map columns/rows, apply
 * client-side filter + sort, then page (offset/limit). `totalRowCount` is the
 * count AFTER filtering but BEFORE paging so the pager shows the right total.
 */
export function buildBlueprintTableData(args: {
  sidecar: DatabaseBlueprintSidecar;
  filters: FilterGroup[];
  sorting: SortDefinition[];
  limit: number;
  offset: number;
}): BlueprintTableDataResult {
  const columns = mapSidecarColumns(args.sidecar);
  const allRows = mapSidecarRowsToObjects(args.sidecar);
  const filteredSorted = applyClientSideFilterSort(allRows, args.filters, args.sorting);
  const totalRowCount = filteredSorted.length;
  const paged = filteredSorted.slice(args.offset, args.offset + args.limit);
  return { rows: paged, columns, totalRowCount };
}
